import * as R from "ramda";
import { Entity } from "common/entities/types";
import { validateGroupColumns } from "common/form/functions/validation";
import { ActionSignature, LayoutActionName } from "common/form/types";
import {
  DEFAULT_WORK_ORDER_ACTION_COLUMNS,
  getActionLayout,
} from "common/record/actions/ui/work-order-action/functions/index";
import { getMissingRequiredEntities } from "common/record/actions/ui/work-order-action/required-entities/functions";
import {
  FormValue,
  WorkOrderActionRecord,
} from "common/record/actions/ui/work-order-action/types";
import {
  getMaxReadingPerDay,
  getMeterType,
  getPreviousReading,
  isValid as validateAssetMeterReading,
} from "common/record/form/content/related/asset-meter-reading/functions";
import { Context } from "common/types/context";
import { Properties, Record } from "common/types/records";
import { hasSystemColumnName } from "x/account-settings/forms/form/work-order-action/functions";

const defaultValue: FormValue = {
  date: undefined,
  signature: undefined,
  page: 0,
  actionRecords: [],
};

export function isValidAssetMeterReading(
  amrProperties: Properties,
  assetMeters: Record[] = [],
  meters: Properties[] = [],
  maxReadingPerDay?: number,
) {
  if (!assetMeters.length || !meters.length) return false;

  const previousReading = getPreviousReading(amrProperties, assetMeters);
  const meterType = getMeterType(amrProperties, assetMeters, meters);
  return validateAssetMeterReading(
    amrProperties,
    previousReading,
    meterType?.id,
    maxReadingPerDay,
  );
}

function isValidProperties(
  context: Context,
  entity: Entity,
  actionName: LayoutActionName,
  properties: Properties,
) {
  const actionLayout = getActionLayout(context, entity, actionName, properties);
  const columns = actionLayout?.columns;
  return validateGroupColumns(entity.columns, properties, columns);
}

export function areAllAssetMeterReadingsValid(
  actionRecords: WorkOrderActionRecord[],
) {
  return R.all((cr) => {
    const { assetMeters, assetMeterReading, meters } = cr;
    const assetMeterId = assetMeterReading?.assetMeterId;
    const value = assetMeterReading?.value;
    const maxReadingPerDay = getMaxReadingPerDay(assetMeterId, assetMeters);
    return (
      !(assetMeterId || value) ||
      isValidAssetMeterReading(
        assetMeterReading,
        assetMeters,
        meters,
        maxReadingPerDay,
      )
    );
  }, actionRecords);
}

function hasRequiredEntities(
  context: Context,
  entity: Entity,
  actionName: LayoutActionName,
  record: WorkOrderActionRecord,
) {
  const missingEntities = getMissingRequiredEntities(
    context,
    entity,
    actionName,
    record,
  );

  return missingEntities.length === 0;
}

export function isValidRecord(
  context: Context,
  entity: Entity,
  actionName: LayoutActionName,
  record: WorkOrderActionRecord,
) {
  const { id, properties } = record;
  if (!id) return false;

  const keysWithoutDefaults = Object.keys(
    R.omit(DEFAULT_WORK_ORDER_ACTION_COLUMNS, properties),
  );
  const hasSystemColumns = hasSystemColumnName(
    keysWithoutDefaults,
    entity.columns,
  );
  if (hasSystemColumns) return false;

  const isValidProps = isValidProperties(
    context,
    entity,
    actionName,
    properties,
  );

  return (
    !!isValidProps && hasRequiredEntities(context, entity, actionName, record)
  );
}

export function isValid(
  context: Context,
  entity: Entity,
  actionName: LayoutActionName,
  signatureSetting: ActionSignature,
  value: FormValue,
) {
  const { date, signature, actionRecords = [] } = value || defaultValue;
  if (signatureSetting === "required" && !signature) {
    return false;
  }
  if (!date) {
    return false;
  }
  if (!areAllAssetMeterReadingsValid(actionRecords)) {
    return false;
  }
  if (actionRecords.length) {
    return actionRecords?.every((record) =>
      isValidRecord(context, entity, actionName, record),
    );
  }
  return true;
}
